<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { useAuthStore } from '../../stores/auth';
import { useEventStore } from '../../stores/event';
import { myUnread, sessionsList, myBanner, dismissBanner, myPolicies, myLinks, linksShown } from '../../api/portal';
import PortalBanner from './PortalBanner.vue';
import HomeComingUp from './HomeComingUp.vue';
import HomeInsurance from './HomeInsurance.vue';
import HomeAdviser from './HomeAdviser.vue';
import HomeLibrary from './HomeLibrary.vue';
import HomeQuickLinks from './HomeQuickLinks.vue';
import PortalOfficeHours from './PortalOfficeHours.vue';
import Loading from '../Loading.vue';
import XlSkeleton from '../xl/XlSkeleton.vue';
import { saveInteractionData } from '../../helpers/interactionHelper';
import { isStaff } from '../../helpers/roles';
import { dateLine, greetingFor, greetingLine, viewerTimeZone } from '../../helpers/greeting';
import { timeState } from '../../helpers/sessionTimes';
import { reportError } from '../../faro';
import { usePortalBroker } from '../../composables/usePortalBroker';

/**
 * Home (XN-4552). Today's date, the greeting, and one sentence beneath it
 * (XN-4563) naming the single most pressing thing. The agency's banner sits
 * beneath the greeting (XN-4564), then Sessions' own Office Hours card (XN-4565).
 * Then, in the ticket's order (XN-4734): Your insurance, Your advisor, Coming up,
 * Quick links, Library. A block with nothing to show is absent.
 */
const auth = useAuthStore();
const eventStore = useEventStore();
const timeZone = viewerTimeZone();
const { broker } = usePortalBroker();

// Ticks once a minute, so a Home left open moves past noon, six and midnight.
const now = ref(new Date());
const tick = setInterval(() => {
  const before = now.value;
  now.value = new Date();
  const ends = (entry) => entry && !timeState(entry, before).ended && timeState(entry, now.value).ended;
  // The desk card asks until it holds a slot still to come: the server reads
  // the end against its own clock, and a few seconds behind the browser's it
  // answers with the slot that has just closed — one try would then leave
  // "closed for today" up, with no next opening, until a reload.
  const deskOver = (desk) => Boolean(desk) && timeState(desk, now.value).ended;
  // A series entry stands for every date, and the desk card for its next
  // slot: when one ends, the next is on the server, not here.
  if (facts.value?.sessions?.some((entry) => entry.series?.dates_left > 1 && ends(entry)) || deskOver(facts.value?.officeHours)) {
    refreshSessions();
  }
}, 60_000);
// Requests can settle after Home has gone; they must then leave no trace.
let disposed = false;
onBeforeUnmount(() => {
  clearInterval(tick);
  disposed = true;
});

const date = computed(() => dateLine(now.value, timeZone));
const greeting = computed(() => greetingFor(auth.userData, now.value, timeZone));
// As Sessions names it on the same card.
const agency = computed(() => eventStore.event?.value?.login_event_name || 'your agency');

// null until all three sources have settled, so the calm line never flashes
// before the real one. A source that failed stays null inside.
const facts = ref(null);
const valueOf = (result) => {
  if (result.status === 'fulfilled') return result.value;
  // Staff previewing are refused /my/* by design; anything else is worth seeing.
  if (result.reason?.response?.status !== 403) reportError(result.reason);
  return null;
};

// A refresh behind what's shown: a failure keeps the list.
async function refreshSessions() {
  try {
    const list = await sessionsList();
    if (disposed) return;
    facts.value = { ...facts.value, sessions: list?.sessions ?? null, complete: list?.complete ?? true, officeHours: list?.office_hours ?? null };
  } catch (error) {
    reportError(error);
  }
}

// A seat saved, or a session gone, in a Coming up panel (XN-4745): Home's copy
// follows at once, so the row's tag and the greeting line agree with it.
const onSeat = ({ id, seat }) => {
  facts.value = { ...facts.value, sessions: facts.value.sessions.map((entry) => (entry.id === id ? { ...entry, seat } : entry)) };
};
const onGone = (id) => {
  const gone = facts.value.sessions.find((entry) => entry.id === id);
  facts.value = { ...facts.value, sessions: facts.value.sessions.filter((entry) => entry.id !== id) };
  // Home holds each series' next date only, and a capped list has more behind
  // it: what replaces the gone date is on the server, so ask again, as Sessions does.
  if (gone && (facts.value.complete === false || Number(gone.series?.dates_left) > 1)) refreshSessions();
};

onMounted(async () => {
  const [unread, list, mine] = await Promise.allSettled([myUnread(), sessionsList(), myPolicies()]);
  const sessions = valueOf(list);
  facts.value = {
    unread: valueOf(unread),
    sessions: sessions?.sessions ?? null,
    complete: sessions?.complete ?? true,
    officeHours: sessions?.office_hours ?? null,
    policies: valueOf(mine)?.policies ?? null,
  };
});

const line = computed(() => facts.value
  && greetingLine({
    unread: facts.value.unread,
    sessions: facts.value.sessions,
    sessionsComplete: facts.value.complete,
    policies: facts.value.policies,
    agency: eventStore.event?.value?.login_event_name || '',
  }, now.value, timeZone));
// The agency's banner (XN-4564), fetched on its own: a failure hides only it.
const banner = ref(null);
const bannerPending = ref(true);
const staff = computed(() => isStaff(auth.userRole));
// Analytics must never surface as an unhandled rejection; saveInteractionData logs and rethrows.
const track = (action) => saveInteractionData(auth.userData, null, 'banners', banner.value?.id, { action }).catch(() => {});
const greetingEl = ref(null);

onMounted(async () => {
  let fetched;
  try {
    fetched = await myBanner();
  } catch (e) {
    console.error('Home: the banner could not be loaded', e);
    return;
  } finally {
    bannerPending.value = false;
  }
  // Never shown, so no impression; and none marked seen to hide the real one.
  if (disposed) return;
  banner.value = fetched;
  // One impression per banner per browser session.
  const key = banner.value && `portal-banner-seen-${banner.value.id}`;
  let seen = false;
  try { seen = !key || !!sessionStorage.getItem(key); if (!seen) sessionStorage.setItem(key, '1'); } catch { /* private mode: at most once per mount */ }
  if (key && !seen) track('impression');
});

// Quick links (XN-4691), fetched on their own: a failure hides only them. A
// failed read of the Links switch counts as on, as the Library's Files one does.
const links = ref([]);
const linksPending = ref(true);
onMounted(async () => {
  try {
    const [shown, list] = await Promise.all([linksShown().catch(() => true), myLinks()]);
    if (!disposed && shown) links.value = list;
  } catch (e) {
    console.error('Home: the quick links could not be loaded', e);
  } finally {
    linksPending.value = false;
  }
});

/**
 * Loading (CP Home had none: the cards came in one by one, each pushing the
 * next down). The date and greeting are known at once; the line under them
 * and the cards wait together, behind one skeleton, and land together — the
 * banner too, which otherwise arrived last and shoved everything down.
 * Under 200ms none of it is seen (Loading, XlSkeleton).
 */
const waiting = computed(() => facts.value === null || bannerPending.value || linksPending.value);

const dismiss = () => {
  const dismissed = banner.value;
  track('dismiss');
  banner.value = null;
  // The focused button is gone; keep keyboard and screen-reader users on the page.
  nextTick(() => greetingEl.value?.focus());
  // Hidden at once; put back if the hub kept it, or it would return on reload.
  dismissBanner(dismissed.id).catch((e) => {
    console.error('Home: the banner could not be dismissed', e);
    if (disposed) return;
    banner.value = dismissed;
    window.loadNotification?.({ message: "That banner couldn't be dismissed. Try again.", error: true });
  });
};
</script>

<template>
  <section class="portal-home">
    <div class="portal-home__greet">
      <p class="portal-home__date" data-testid="portal-date">{{ date }}</p>
      <h1 ref="greetingEl" tabindex="-1" class="portal-home__hello" data-testid="portal-greeting">{{ greeting }}</h1>
      <p class="portal-home__line" data-testid="portal-line">
        <XlSkeleton v-if="waiting" width="min(420px, 80%)" height="0.8em" radius="7px" class="portal-home__line-sk" />
        <template v-else-if="line">
          <RouterLink v-if="line.to" :to="line.to" class="portal-home__link"><b v-if="line.lead">{{ line.lead }}</b>{{ line.lead ? line.text.slice(line.lead.length) : line.text }}</RouterLink>
          <template v-else><b v-if="line.lead">{{ line.lead }}</b>{{ line.lead ? line.text.slice(line.lead.length) : line.text }}</template>
        </template>
      </p>
    </div>
    <Loading v-if="waiting" variant="rows" :rows="3" data-testid="home-loading" />
    <template v-else>
      <PortalBanner v-if="banner" :banner="banner" :can-dismiss="!staff" @dismiss="dismiss" @cta="track('cta_click')" />
      <PortalOfficeHours v-if="facts?.officeHours" :office-hours="facts.officeHours" :agency="agency" :time-zone="timeZone" :now="now" />
      <HomeInsurance v-if="facts?.policies" :policies="facts.policies" :now="now" />
      <HomeAdviser :broker="broker" :unread-messages="facts?.unread?.messages ?? null" :agency="agency" />
      <HomeComingUp v-if="facts?.sessions" :sessions="facts.sessions" :now="now" :time-zone="timeZone" @seat="onSeat" @gone="onGone" />
      <HomeQuickLinks v-if="links.length" :links="links" />
      <HomeLibrary />
    </template>
  </section>
</template>

<style scoped>
/* Prototype `.greet` and `.greet p` / `.hl` (build 29 Sep 15:06). */
.portal-home__greet { margin-bottom: 26px; }

.portal-home__date {
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--xl-color-outline-variant);
  margin: 0 0 10px;
}

.portal-home__hello {
  font-size: 40px;
  line-height: 1.1;
  font-weight: 600;
  letter-spacing: -0.03em;
  margin: 0 0 10px;
  overflow-wrap: anywhere;
}

.portal-home__line {
  font-size: 17.5px;
  line-height: 1.5;
  color: var(--xl-color-on-surface-variant);
  margin: 0;
  min-height: 1.5em; /* one line, held so the banner and cards below do not jump */
  max-width: 56ch;
  overflow-wrap: anywhere;
}

/* Prototype `.hl` (build 12 Oct 17:52): only the lead is dark and bold; the rest
   of the line stays regular, link or not (XN-4736). */
.portal-home__line-sk { width: auto; vertical-align: middle; }
.portal-home__line b { color: var(--xl-color-on-surface); font-weight: 600; }
.portal-home__link {
  color: inherit;
  text-decoration: none;
}
.portal-home__link:hover { text-decoration: underline; }
.portal-home__link:focus-visible { outline: 2px solid var(--xl-color-primary); outline-offset: 2px; border-radius: 4px; }

@media (max-width: 480px) {
  .portal-home__hello { font-size: 30px; }
}
</style>
